import api from '../../../services/api';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import type { OrderSaveConfirmation } from './orderProductsApi';
import {
  asArray,
  asNumber,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from '../../../dto/common';
import { OrderDto, normalizeOrder, normalizeOrders } from '../dto';
import {
  normalizeWZPrefillForOrder,
  type WZPrefillForOrderDto,
} from '../../../dto/wz';
import type {
  OrderMarginHistoryDetails,
  OrderMarginHistoryEntry,
  OrderMarginHistoryLine,
  OrderMarginHistoryLineImage,
} from '../../../types/order';
import {
  getOrderBaseCurrency,
  normalizeCreateOrderProducts,
  OrderProductLineDraft,
} from '../orderProductLineTotals';

export type OrderMarginRange = 'low' | 'normal' | 'high';

export interface OrdersListQuery {
  status?: string[];
  customerId?: string;
  accountManagerId?: string;
  startDate?: string;
  endDate?: string;
  marginRange?: OrderMarginRange;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isQuote?: boolean;
}

export interface OrderSettingsDto {
  currency?: string;
}

export type OrderDocumentLanguage = 'pl' | 'en';
export type OrderDocumentConversionUnit = 'pcs' | 'm2' | 'm3';

function ordersListParams(query: OrdersListQuery): URLSearchParams {
  const params = new URLSearchParams();

  if (query.status && query.status.length > 0) {
    params.append('status', query.status.join(','));
  }

  if (query.customerId) {
    params.append('customerId', query.customerId);
  }

  if (query.accountManagerId) {
    params.append('accountManagerId', query.accountManagerId);
  }

  params.append('isQuote', String(Boolean(query.isQuote)));

  if (query.startDate) {
    params.append('startDate', query.startDate);
  }

  if (query.endDate) {
    params.append('endDate', query.endDate);
  }

  if (query.marginRange === 'low') {
    params.append('marginMax', '15');
  }

  if (query.marginRange === 'normal') {
    params.append('marginMin', '15');
    params.append('marginMax', '30');
  }

  if (query.marginRange === 'high') {
    params.append('marginMin', '30');
  }

  if (query.search) {
    params.append('search', query.search);
  }

  params.append('sortBy', query.sortBy || 'orderDate');
  params.append('sortOrder', query.sortOrder || 'desc');
  return params;
}

export async function fetchOrders(query: OrdersListQuery = {}, entity = 'orders.list'): Promise<OrderDto[]> {
  return (await fetchOrdersWithRows(query, entity)).orders;
}

/**
 * The order list plus the response rows as sent: a screen that reads fields
 * outside `OrderDto` (the sales cut of „Wynik firmy”) shares one request with
 * the DTO reader instead of loading the same list twice (2026-10-30).
 */
export async function fetchOrdersWithRows(
  query: OrdersListQuery = {},
  entity = 'orders.list',
): Promise<{ orders: OrderDto[]; rows: unknown[] }> {
  const params = ordersListParams(query);
  const response = await api.get(`/orders${params.toString() ? `?${params.toString()}` : ''}`);
  return { orders: normalizeOrders(response.data, entity), rows: Array.isArray(response.data) ? response.data : [] };
}

export async function fetchOrderDetail(orderId: string, entity = 'orders.detail'): Promise<OrderDto> {
  const response = await api.get(`/orders/${orderId}`);
  return normalizeOrder(response.data, entity);
}

export async function fetchOrderSettings(): Promise<OrderSettingsDto> {
  const response = await api.get('/settings');
  return response.data;
}

export interface CreateOrderValues extends Record<string, unknown> {
  currency?: string;
  orderDate?: { toISOString: () => string };
  expectedDelivery?: { toISOString: () => string };
  customerPaymentDueDate?: { toISOString: () => string };
  customerPaymentNotes?: string | null;
  products?: OrderProductLineDraft[];
}

interface CreateOrderPayloadOptions {
  includeProductSalePrices?: boolean;
}

export function buildCreateOrderPayload(
  values: CreateOrderValues,
  preferredCurrency = 'PLN',
  options: CreateOrderPayloadOptions = {},
): Record<string, unknown> {
  const products = values.products || [];
  const includeProductSalePrices = options.includeProductSalePrices !== false;
  const baseCurrency = getOrderBaseCurrency(
    products,
    values.currency || preferredCurrency || 'PLN'
  );
  const productCurrencies = Array.from(
    new Set(products.map((product) => product.currency).filter(Boolean))
  );
  const orderCurrency = productCurrencies.length > 1
    ? values.currency || preferredCurrency || 'PLN'
    : baseCurrency;

  return {
    ...values,
    currency: orderCurrency,
    orderDate: values.orderDate?.toISOString(),
    expectedDelivery: values.expectedDelivery?.toISOString(),
    customerPaymentDueDate: values.customerPaymentDueDate?.toISOString(),
    customerPaymentNotes: values.customerPaymentNotes || null,
    products: normalizeCreateOrderProducts(products, baseCurrency, includeProductSalePrices),
  };
}

/** `POST /orders` with a body from `buildCreateOrderPayload`; the new ZK starts as Szkic (NEW_INQUIRY). */
export async function createOrder(payload: Record<string, unknown>): Promise<OrderDto> {
  const response = await api.post('/orders', payload);
  return normalizeOrder(response.data, 'orders.create');
}

/**
 * WP12 B1 (1.14): an order on transports or with unreleased WZ is deleted
 * after one confirmed warning (it is unlinked, its draft WZ deleted). With
 * `confirmation` the shared dialog asks for the reason; a cancelled dialog
 * resolves to `false` (nothing deleted). A released WZ or an approved invoice
 * still refuses with `archiveAvailable` — see `isArchiveOffered`.
 */
export async function deleteOrder(orderId: string, confirmation?: OrderSaveConfirmation): Promise<boolean> {
  if (!confirmation) {
    await api.delete(`/orders/${orderId}`);
    return true;
  }
  const response = await submitWithOperationalException(
    (body) => api.delete(`/orders/${orderId}`, { data: body }),
    {},
    confirmation.modal,
    confirmation.t,
  );
  return response !== null;
}

/** The delete refusal offers "Anuluj i ukryj" (backend `archiveAvailable`). */
export function isArchiveOffered(error: unknown): boolean {
  return (error as { response?: { data?: { archiveAvailable?: unknown } } })?.response?.data?.archiveAvailable === true;
}

/**
 * WP12 B1 "Anuluj i ukryj": the order leaves lists, the forecast and the
 * allocation queue; invoices and the result stay. Always a confirmed warning
 * (ORDER_ARCHIVED). Resolves to `false` when the dialog was cancelled.
 */
export async function archiveOrder(orderId: string, confirmation: OrderSaveConfirmation): Promise<boolean> {
  const response = await submitWithOperationalException(
    (body) => api.post(`/orders/${orderId}/archive`, body),
    {},
    confirmation.modal,
    confirmation.t,
  );
  return response !== null;
}

/** Admin only: restore an archived order. */
export async function unarchiveOrder(orderId: string): Promise<void> {
  await api.post(`/orders/${orderId}/unarchive`);
}

/**
 * Header save. An invoiced order asks for ORDER_CHANGED_AFTER_INVOICE when an
 * invoice-relevant field changes (WP1 part 2); with `confirmation` the shared
 * dialog asks for the reason and a cancelled dialog resolves to `false`.
 */
export async function updateOrder(
  orderId: string,
  payload: Record<string, unknown>,
  confirmation?: OrderSaveConfirmation,
): Promise<boolean> {
  if (!confirmation) {
    await api.put(`/orders/${orderId}`, payload);
    return true;
  }
  const response = await submitWithOperationalException(
    (body) => api.put(`/orders/${orderId}`, body),
    payload,
    confirmation.modal,
    confirmation.t,
  );
  return response !== null;
}

export async function fetchSuggestedManualOrderNumber(orderDate?: string): Promise<string> {
  const response = await api.get('/orders/suggest-number', {
    params: orderDate ? { orderDate } : undefined,
  });
  return response.data.suggestedNumber;
}

export async function convertQuoteToOrder(orderId: string): Promise<unknown> {
  const response = await api.post(`/orders/${orderId}/convert-quote`);
  return response.data;
}

export async function fetchOrderMarginHistory(orderId: string, entity = 'orders.marginHistory'): Promise<OrderMarginHistoryEntry[]> {
  const response = await api.get(`/orders/${orderId}/margin-history`);
  return normalizeOrderMarginHistory(response.data, entity);
}

export async function fetchOrderProductSuggestions(): Promise<unknown[]> {
  const response = await api.get('/orders/product-names');
  return response.data;
}

/**
 * Read-only "what would generating a WZ from this order fill in right now"
 * projection — remaining quantity per line (netted against this order's
 * other non-cancelled WZ documents), WITH productVariantId carried through.
 * The manual-create wizard's order-picker prefill (WZNew.tsx) reads this
 * instead of deriving quantities from raw order.products, so it can never
 * drift from what POST /wz/generate/:orderId (createWZFromOrder) would
 * actually produce — see backend/src/utils/wzOrderLineCoverage.js.
 */
export async function fetchWZPrefillForOrder(orderId: string): Promise<WZPrefillForOrderDto> {
  const response = await api.get(`/wz/prefill/${orderId}`);
  return normalizeWZPrefillForOrder(response.data);
}

export async function downloadOrderPdf(
  orderId: string,
  language: OrderDocumentLanguage,
  conversionUnit?: OrderDocumentConversionUnit,
): Promise<Blob> {
  const params = new URLSearchParams({ lang: language });
  if (conversionUnit) {
    params.set('conversionUnit', conversionUnit);
  }

  const response = await api.get(`/orders/${orderId}/pdf?${params.toString()}`, {
    responseType: 'blob',
  });

  return response.data;
}

function normalizeOrderMarginHistory(raw: unknown, entity: string): OrderMarginHistoryEntry[] {
  return asArray(raw, (item, index) => normalizeOrderMarginHistoryEntry(item, `${entity}[${index}]`));
}

function normalizeOrderMarginHistoryEntry(raw: unknown, entity: string): OrderMarginHistoryEntry {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }

  const changedByRecord = asRecord(record?.changedBy);

  if (record?.changedBy != null && !changedByRecord) {
    reportDtoAnomaly(entity, 'changedBy', record?.changedBy);
  }

  return {
    id: asString(record?.id),
    orderId: asString(record?.orderId),
    revenue: asNumber(record?.revenue),
    totalCost: asNumber(record?.totalCost),
    margin: asNumber(record?.margin),
    marginPercent: asNumber(record?.marginPercent),
    reason: asString(record?.reason),
    changedAt: asString(record?.changedAt),
    changedBy: changedByRecord
      ? {
          id: asString(changedByRecord.id),
          name: asString(changedByRecord.name),
          email: asOptionalString(changedByRecord.email),
        }
      : null,
    details: normalizeOrderMarginHistoryDetails(record?.details),
  };
}

const MARGIN_HISTORY_LINE_CHANGES: OrderMarginHistoryLine['change'][] = ['ADDED', 'UPDATED', 'REMOVED'];

// Missing or unknown values in a history image stay unknown (null), never 0.
const asHistoryNumber = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

function normalizeOrderMarginHistoryLineImage(raw: unknown): OrderMarginHistoryLineImage | null {
  const record = asRecord(raw);
  if (!record) return null;
  return {
    unitPrice: asHistoryNumber(record.unitPrice),
    currency: asOptionalString(record.currency) ?? null,
    quantity: asHistoryNumber(record.quantity),
    unit: asOptionalString(record.unit) ?? null,
    saleQuantity: asHistoryNumber(record.saleQuantity),
    saleUnit: asOptionalString(record.saleUnit) ?? null,
    totalPrice: asHistoryNumber(record.totalPrice),
  };
}

/** `details` is optional JSON written by the backend; an absent or malformed value is simply no details. */
export function normalizeOrderMarginHistoryDetails(raw: unknown): OrderMarginHistoryDetails | null {
  const record = asRecord(raw);
  if (!record) return null;
  const lines = Array.isArray(record.lines)
    ? record.lines
      .map((line): OrderMarginHistoryLine | null => {
        const lineRecord = asRecord(line);
        const change = asString(lineRecord?.change) as OrderMarginHistoryLine['change'];
        if (!lineRecord || !MARGIN_HISTORY_LINE_CHANGES.includes(change)) return null;
        return {
          orderProductId: asOptionalString(lineRecord.orderProductId) ?? null,
          name: asOptionalString(lineRecord.name) ?? null,
          change,
          before: normalizeOrderMarginHistoryLineImage(lineRecord.before),
          after: normalizeOrderMarginHistoryLineImage(lineRecord.after),
        };
      })
      .filter((line): line is OrderMarginHistoryLine => line !== null)
    : undefined;
  return {
    ...(lines ? { lines } : {}),
    ...(asOptionalString(record.fromStatus) ? { fromStatus: asString(record.fromStatus) } : {}),
    ...(asOptionalString(record.toStatus) ? { toStatus: asString(record.toStatus) } : {}),
    ...(asOptionalString(record.reason) ? { reason: asString(record.reason) } : {}),
  };
}
